import React, { useLayoutEffect, useRef, useState } from 'react';
import { Alert, Keyboard, TextInput, View } from 'react-native';
import { CommonActions } from '@react-navigation/native';
import emoji from 'node-emoji';
import { AppView, rem } from 'design-system';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import { KeyboardAvoidingContainerNew } from 'design-system/KeyboardAvoidingContainerNew';
import StatusBar from 'design-system/StatusBar';
import { useFetchOld } from 'hooks';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { updatePot } from '../actions';
import { SingleInputScreenFlows } from '../types';
const getTitleFromFlow = (flow) => {
    switch (flow) {
        case SingleInputScreenFlows.editPotName:
            return 'Edit Name';
        default:
            return '';
    }
};
const getBtnTextFromFlow = (flow) => {
    switch (flow) {
        case SingleInputScreenFlows.editPotName:
            return 'Continue';
        default:
            return '';
    }
};
const SingleInputScreen = ({ route, navigation }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const pot = route.params?.pot;
    const flow = route.params?.flow;
    const defaultValue = route.params?.defaultValue;
    const btnText = getBtnTextFromFlow(flow);
    const [, , loading, fetch] = useFetchOld({ throws: true });
    const [name, setName] = useState(defaultValue || '');
    const textInputRef = useRef(null);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: getTitleFromFlow(flow),
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary, colors.text.white, flow]);
    useAutoFocusInput(textInputRef);
    const handleNext = async () => {
        if (!name) {
            Alert.alert(emoji.emojify('Hold on :dizzy:'), 'You must enter a name.', [
                {
                    text: 'Ok',
                    onPress: () => {
                        textInputRef.current?.focus();
                    },
                },
            ]);
            return;
        }
        Keyboard.dismiss();
        if (defaultValue === name) {
            navigation.pop();
        }
        switch (flow) {
            case SingleInputScreenFlows.editPotName: {
                try {
                    const response = await fetch(updatePot({
                        ...pot,
                        name,
                    }));
                    if (response) {
                        const potScreen = navigation.getState().routes.find((route) => route.name === 'Pot');
                        const potSettingsScreen = navigation.getState().routes.find((route) => route.name === 'PotSettings');
                        if (potScreen) {
                            navigation.dispatch({
                                ...CommonActions.setParams({
                                    screenParams: {
                                        ...pot,
                                        name,
                                    },
                                }),
                                source: potScreen.key,
                            });
                        }
                        if (potSettingsScreen) {
                            navigation.dispatch({
                                ...CommonActions.setParams({
                                    pot: {
                                        ...pot,
                                        name,
                                    },
                                }),
                                source: potSettingsScreen.key,
                            });
                        }
                        navigation.goBack();
                    }
                }
                catch {
                    // nothing to do: middleware shows the error
                }
                break;
            }
            default:
                break;
        }
    };
    const onChangeText = (data) => {
        setName(data);
    };
    return (<AppView avoidHeader>
      <StatusBar barStyle="dark-content" animated/>
      <KeyboardAvoidingContainerNew flexContainer withButtonSpace>
        <View style={styles.container}>
          <TextInput value={name} autoFocus={false} ref={textInputRef} autoCorrect={false} returnKeyType="done" placeholder="Choose Name" allowFontScaling={false} style={styles.textInput} onChangeText={onChangeText} selectionColor={colors.text.selection} onSubmitEditing={handleNext} underlineColorAndroid="transparent" placeholderTextColor={colors.text.placeholder}/>
        </View>
      </KeyboardAvoidingContainerNew>
      <KeyboardAvoidingButton brand title={btnText} onPress={handleNext} loading={loading} withFrame={false}/>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        flex: 1,
        justifyContent: 'center',
    },
    textInput: {
        color: colors.text.primary,
        textAlign: 'center',
        fontSize: rem(24),
        fontFamily: 'Montserrat-Light',
    },
}));
export default SingleInputScreen;
